import { RefObject } from "react"

import { type SvgIntrinsicGeometry } from "./svg_geometry"

const OVERSCROLL_MARGIN = 40

// Opt-in "recolor" treatment for unavailable tables (mobile public booking, Figma 1283-10): a
// re-coloured grey state at full opacity rather than the default faded artwork.
export const DISABLED_RECOLOR_FILL = "#f1f2f3"
export const DISABLED_RECOLOR_STROKE = "#929baa"

// Paint (or with empty strings, clear) the table element and its sub-elements. Clearing restores the
// original SVG artwork so a table that becomes available again is no longer grey.
export const paintTable = (element: Element, fill: string, stroke: string) => {
  const apply = (target: SVGElement) => {
    target.style.fill = fill
    target.style.stroke = stroke
  }
  apply(element as SVGElement)
  element.querySelectorAll("*").forEach((child) => apply(child as SVGElement))
}

export const constrainPan = (
  newPan: { x: number; y: number },
  currentZoom: number,
  svgWrapperRef: RefObject<HTMLDivElement>,
  containerRef: RefObject<HTMLDivElement>,
  baseScale: number = 1,
  geometry?: SvgIntrinsicGeometry | null,
): { x: number; y: number } => {
  if (!svgWrapperRef.current || !containerRef.current) return newPan

  const wrapper = svgWrapperRef.current.parentElement
  if (!wrapper) return newPan

  const containerRect = wrapper.getBoundingClientRect()

  let svgWidth: number
  let svgHeight: number

  if (geometry) {
    svgWidth = geometry.width
    svgHeight = geometry.height
  } else {
    const container = containerRef.current
    const svgElement = container.querySelector("svg")
    if (!svgElement) return newPan

    const viewBox = svgElement.viewBox?.baseVal
    svgWidth = viewBox?.width || parseFloat(svgElement.getAttribute("width") || "0")
    svgHeight = viewBox?.height || parseFloat(svgElement.getAttribute("height") || "0")
  }

  const effectiveScale = (currentZoom / 100) * baseScale
  const scaledWidth = svgWidth * effectiveScale
  const scaledHeight = svgHeight * effectiveScale

  const diffX = containerRect.width - scaledWidth
  const diffY = containerRect.height - scaledHeight

  const minX = Math.min(0, diffX) - OVERSCROLL_MARGIN
  const maxX = Math.max(0, diffX) + OVERSCROLL_MARGIN
  const minY = Math.min(0, diffY) - OVERSCROLL_MARGIN
  const maxY = Math.max(0, diffY) + OVERSCROLL_MARGIN

  return {
    x: Math.max(minX, Math.min(maxX, newPan.x)),
    y: Math.max(minY, Math.min(maxY, newPan.y)),
  }
}

export const getTableElement = (containerRef: RefObject<HTMLDivElement>, tableId: string): SVGElement | null => {
  if (!containerRef.current) return null
  const svgElement = containerRef.current.querySelector("svg")
  if (!svgElement) return null
  return svgElement.querySelector(`[data-tabb-table="${tableId}"]`)
}

export const highlightTableElement = (element: SVGElement | null, isHighlighted: boolean) => {
  if (!element) return

  if (isHighlighted) {
    element.style.outline = "3px solid #2596fa"
    element.style.outlineOffset = "2px"
  } else {
    element.style.outline = ""
  }
}
